import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, money } from "@/lib/api";
import { DocumentUploader } from "@/components/DocumentUploader";

export default function Verification() {
  const { user } = useAuth();
  const [cases, setCases] = useState([]);
  const [caseError, setCaseError] = useState("");
  const [reports, setReports] = useState([]);
  useEffect(() => {
    let active = true;
    if (!user) { setCases([]); setReports([]); setCaseRec(null); return; }
    Promise.all([api.get("/verification/cases/mine"), api.get("/verification/reports/mine")]).then(([c,r]) => { if(active) { setCases(c.data); setReports(r.data); } }).catch(() => { if(active) setCaseError("Your cases could not be loaded. Refresh to try again."); });
    return () => { active = false; };
  }, [user]);
  const [scopes, setScopes] = useState([]);
  const [decl, setDecl] = useState(null);
  const [scopeId, setScopeId] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [caseRec, setCaseRec] = useState(null);
  const [confirmations, setConfirmations] = useState([]);
  const [name, setName] = useState("");
  const [sig, setSig] = useState("");
  const [msg, setMsg] = useState("");
  const actionRef = useRef(false);
  const [action, setAction] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    let active = true;

    const load = async () => {
      try {
        const [scopeResult, declarationResult] = await Promise.allSettled([
          api.get("/verification/scopes"),
          api.get("/verification/declarations/current"),
        ]);

        if (!active) return;

        if (scopeResult.status === "rejected") {
          throw scopeResult.reason;
        }

        setScopes(scopeResult.value.data);

        if (declarationResult.status === "fulfilled") {
          setDecl(declarationResult.value.data);
        }
      } catch (error) {
        if (active) {
          setLoadError("Verification services could not be loaded. Please try again later.");
        }
      } finally {
        if (active) setLoading(false);
      }
    };

    load();
    return () => { active = false; };
  }, []);

  const createCase = async () => {
    if (actionRef.current || caseRec) return;
    actionRef.current = true; setAction("create"); setMsg("");
    try {
      const { data } = await api.post("/verification/cases", { scope_id: scopeId, accepted_scope: accepted, document_categories: scopes.find(s => s.id === scopeId)?.document_categories || [] });
      setCaseRec(data); setCases(current => [data, ...current]); setMsg(data.warning || "Case created. The legal declaration must now be accepted.");
    } catch (e) { setMsg(e?.response?.data?.detail || "Unable to create case."); }
    finally { actionRef.current = false; setAction(""); }
  };

  const acceptDeclaration = async () => {
    if (actionRef.current || caseRec?.declaration_accepted) return;
    actionRef.current = true; setAction("declaration"); setMsg("");
    try {
      const { data } = await api.post(`/verification/cases/${caseRec.id}/declaration`, {
        declaration_id: decl.id, confirmations, typed_legal_name: name, signature: sig,
      });
      setMsg(`Declaration version ${data.declaration_version} accepted and saved.${data.warning ? " " + data.warning : ""}`);
      setCaseRec({ ...caseRec, declaration_accepted: true, status: "awaiting_payment" });
    } catch (e) { setMsg(e?.response?.data?.detail || "Unable to accept declaration."); }
    finally { actionRef.current = false; setAction(""); }
  };

  const pay = async () => {
    if (actionRef.current) return;
    actionRef.current = true; setAction("payment"); setMsg("");
    try {
      const { data } = await api.post("/payments/checkout", {
        item_type: "verification_case", item_id: caseRec.id, origin_url: window.location.origin,
      });
      if (!data.checkout_url) throw new Error("Checkout URL missing");
      window.location.href = data.checkout_url;
    } catch (e) { setMsg(e?.response?.data?.detail || "Unable to start payment. Your case remains saved; please try again or contact support."); }
    finally { actionRef.current = false; setAction(""); }
  };

  const download = async () => {
    if (actionRef.current) return;
    actionRef.current = true; setAction("download"); setMsg("");
    let url;
    try {
      const res = await api.get(`/verification/cases/${caseRec.id}/declaration/download`, { responseType: "blob" });
      url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `declaration-${caseRec.case_number}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setMsg("Declaration download started.");
    } catch (e) {
      setMsg("Unable to download the declaration. Please try again or contact support.");
    } finally {
      if (url) { const downloadUrl = url; setTimeout(() => URL.revokeObjectURL(downloadUrl), 1000); }
      actionRef.current = false; setAction("");
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-5 py-10" data-testid="verification-page">
      <p className="overline">Document verification</p>
      <h1 className="mt-3 text-3xl font-semibold">Document verification services</h1>
      <p className="mt-3 max-w-3xl text-sm text-slate-600">
        We review document completeness, consistency and indicators of alteration, and record issuing-source enquiries where included in your purchased scope. You receive a written report of checks and findings.
        EuropEdu verification is not a replacement for official Home Office right-to-work checks and cannot
        definitively authenticate a Certificate of Sponsorship through a public database.
      </p>

      <section className="card p-6 mt-6"><h2 className="font-semibold">What happens when you apply</h2><ol className="list-decimal pl-5 text-sm text-slate-600 mt-3 space-y-2"><li>Choose a service and review the exact checks, exclusions, price and turnaround below.</li><li>Create your application and accept its legal declaration.</li><li>Pay securely, then upload the documents for your chosen service.</li><li>Follow triage, analysis and review status in your account. Download the report when issued.</li></ol><p className="text-xs text-slate-500 mt-3">Turnaround starts when payment and complete readable documents are received. Further evidence requests can extend it.</p></section>
      {!user && <Link className="btn btn-primary mt-5" to="/login?next=%2Fverify">Sign in to apply or track a case</Link>}
      {caseError && <p role="alert" className="text-red-700 mt-4">{caseError}</p>}
      {user && cases.length > 0 && <section className="card p-6 mt-6"><h2 className="font-semibold">My verification applications</h2><ul className="mt-3 space-y-3">{cases.map(c => <li key={c.id}><button className="text-blue-800 underline text-sm" onClick={() => {setCaseRec(c);setMsg("");setConfirmations([]);setName("");setSig("");}}>{c.case_number} — {c.scope_name}</button><p className="text-xs text-slate-500">{c.status.replace(/_/g," ")} · Payment {c.payment_status}</p></li>)}</ul><button className="btn btn-ghost mt-4" onClick={() => {setCaseRec(null);setAccepted(false);setScopeId("");setConfirmations([]);setName("");setSig("");}}>Start a new application</button></section>}
      {caseRec && <section className="card p-6 mt-5"><h2 className="font-semibold">{caseRec.case_number}: {caseRec.scope_name}</h2><p className="text-sm mt-2">Status: {caseRec.status.replace(/_/g," ")} · Payment: {caseRec.payment_status}</p><p className="text-sm text-slate-600 mt-2">Agreed price: {money(caseRec.scope_snapshot?.price_pence)} · {caseRec.scope_snapshot?.turnaround}</p>{caseRec.declaration_accepted && caseRec.payment_status === "paid" && <button className="btn btn-ghost mt-3" disabled={Boolean(action)} onClick={download}>Download accepted declaration</button>}{caseRec.triage?.missing_documents?.length > 0 && <p className="text-sm mt-3">Further documents requested: {caseRec.triage.missing_documents.join(", ")}</p>}{reports.filter(r=>r.case_id===caseRec.id).map(r=><button key={r.id} className="btn btn-primary mt-3" onClick={async()=>{try{const res=await api.get(`/verification/reports/${r.id}/download`,{responseType:"blob"});const url=URL.createObjectURL(res.data);const a=document.createElement("a");a.href=url;a.download=`verification-${r.reference}.pdf`;a.click();setTimeout(()=>URL.revokeObjectURL(url),1000);}catch{setMsg("Unable to download report. Please try again.");}}}>Download verification report</button>)}</section>}
      {loading && (
        <section className="card p-6 mt-8 text-sm text-slate-600" data-testid="verification-loading">
          Loading available verification services…
        </section>
      )}

      {loadError && (
        <section className="card p-6 mt-8 text-sm text-red-700" data-testid="verification-error">
          {loadError}
        </section>
      )}

      {!loading && !loadError && scopes.length === 0 && (
        <section className="card p-6 mt-8 text-sm text-slate-600" data-testid="verification-empty">
          No verification services are currently published. Please contact EuropEdu if you need assistance.
        </section>
      )}

      <section className="mt-8 grid gap-4 md:grid-cols-2" data-testid="scope-list">
        {scopes.map((s) => (
          <label key={s.id} className={`card p-5 cursor-pointer ${scopeId === s.id ? "border-[#1e3a8a]" : ""}`} data-testid={`scope-${s.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="font-semibold">{s.name}</h2>
                <p className="text-sm text-slate-600 mt-1">{money(s.price_pence)} · {s.turnaround}</p>
              </div>
              <input type="radio" disabled={Boolean(action) || Boolean(caseRec)} name="scope" checked={scopeId === s.id} onChange={() => setScopeId(s.id)} />
            </div>
            <p className="mt-3 text-xs text-slate-600"><strong>Included:</strong> {s.included_checks.join("; ")}</p>
            <p className="mt-1 text-xs text-slate-600"><strong>Excluded:</strong> {s.excluded_checks.join("; ")}</p>
            <p className="mt-1 text-xs text-slate-500">{s.limitations}</p>
          </label>
        ))}
      </section>

      {user && scopes.length > 0 && !decl && <p className="mt-4 text-sm text-amber-800">Applications open when the current service declaration is published. Please contact EuropEdu for assistance.</p>}
      {user && scopes.length > 0 && (
        <>
          <label className="flex gap-2 items-start text-sm mt-6">
            <input data-testid="accept-scope-checkbox" disabled={Boolean(action) || Boolean(caseRec)} type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} />
            I accept the scope and limitations of the selected verification service.
          </label>
          <button data-testid="create-case-button" disabled={!decl || !scopeId || !accepted || loading || Boolean(loadError) || Boolean(action) || Boolean(caseRec)} onClick={createCase} className="btn btn-primary mt-4">{action === "create" ? "Creating case…" : caseRec ? "Case created" : "Create verification case"}</button>
        </>
      )}

      {caseRec?.payment_status === "paid" && <DocumentUploader key={caseRec.id} caseId={caseRec.id} closed={["completed", "cancelled", "report_issued", "out_of_scope"].includes(caseRec.status)} />}
      {caseRec && caseRec.payment_status !== "paid" && <p className="mt-4 text-sm text-slate-600">Documents can be uploaded after confirmed service payment.</p>}

      {caseRec && decl && (caseRec.status === "awaiting_declaration" || caseRec.status === "awaiting_payment") && (
        <section className="card p-6 mt-10" data-testid="declaration-block">
          <p className="overline">{decl.title} — version {decl.version}</p>
          <p className="pill bg-amber-50 text-amber-800 mt-2" data-testid="solicitor-review-badge">Solicitor review required</p>
          <pre className="mt-4 whitespace-pre-wrap text-xs text-slate-700 max-h-72 overflow-y-auto border border-slate-200 rounded-lg p-4">{decl.body}</pre>
          <div className="mt-4 space-y-2">
            {decl.required_confirmations.map((c) => (
              <label key={c} className="flex gap-2 items-start text-sm">
                <input type="checkbox" disabled={Boolean(action) || caseRec.declaration_accepted} data-testid={`confirmation-${decl.required_confirmations.indexOf(c)}`}
                  checked={confirmations.includes(c)}
                  onChange={(e) => setConfirmations(e.target.checked ? [...confirmations, c] : confirmations.filter((x) => x !== c))} />
                {c}
              </label>
            ))}
          </div>
          <div className="grid gap-3 sm:grid-cols-2 mt-4">
            <label className="block text-sm">Typed legal name<input data-testid="legal-name-input" disabled={Boolean(action) || caseRec.declaration_accepted} className="field mt-1" value={name} onChange={(e) => setName(e.target.value)} /></label>
            <label className="block text-sm">Signature<input data-testid="signature-input" disabled={Boolean(action) || caseRec.declaration_accepted} className="field mt-1" value={sig} onChange={(e) => setSig(e.target.value)} /></label>
          </div>
          <div className="flex flex-wrap gap-3 mt-4">
            <button data-testid="accept-declaration-button" disabled={Boolean(action) || caseRec.declaration_accepted || !name.trim() || !sig.trim() || !decl.required_confirmations.every((c) => confirmations.includes(c))} onClick={acceptDeclaration} className="btn btn-primary">{action === "declaration" ? "Recording declaration…" : caseRec.declaration_accepted ? "Declaration accepted" : "Accept declaration"}</button>
            {caseRec.declaration_accepted && <>
              <button data-testid="download-declaration-button" disabled={Boolean(action)} onClick={download} className="btn btn-ghost">{action === "download" ? "Downloading…" : "Download declaration PDF"}</button>
              {caseRec.payment_status !== "paid" && <button data-testid="pay-case-button" disabled={Boolean(action)} onClick={pay} className="btn btn-ghost">{action === "payment" ? "Opening checkout…" : "Pay securely with Stripe"}</button>}
            </>}
          </div>
        </section>
      )}
      {msg && <p data-testid="verification-message" role="status" aria-live="polite" className="mt-4 text-sm text-slate-800">{msg}</p>}
    </div>
  );
}

